import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserRole } from '@dmax/shared';

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setAccessToken: (accessToken) => set({ accessToken }),

      login: (user, accessToken) =>
        set({
          user,
          accessToken,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'dmax-admin-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Role check helpers
export function useIsOwner(): boolean {
  const user = useAuthStore((state) => state.user);
  return user?.role === 'owner';
}

export function useIsAdmin(): boolean {
  const user = useAuthStore((state) => state.user);
  return user?.role === 'owner' || user?.role === 'manager';
}

export function useIsContentManager(): boolean {
  const user = useAuthStore((state) => state.user);
  return ['owner', 'manager', 'marketing'].includes(user?.role ?? '');
}
